import { useEffect, useState } from 'react';
import { useLazyGetProductCategoryListQuery } from '../../../redux/api/handBooksApi/chemicalization/productionCategory';

interface ProductCategory {
  id: number;
  nameRu: string | null;
  nameKy: string | null;
  parent: { id: number; childId?: number | null } | null;
  children?: Array<{
    id: number;
    nameRu: string | null;
    nameKy: string | null;
  }> | null;
}

interface UseProductCategoriesProps {
  initialCategoryId: number | null;
  initialChildId: number | null;
}

export const useProductCategories = ({ initialCategoryId, initialChildId }: UseProductCategoriesProps) => {
  const [getProductCategories] = useLazyGetProductCategoryListQuery();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(initialCategoryId);
  const [selectedChild, setSelectedChild] = useState<number | null>(initialChildId);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (categories.length > 0) {
      console.log('Initializing with:', { initialCategoryId, initialChildId });
      if (initialCategoryId) {
        const categoryExists = categories.some(c => c.id === initialCategoryId);
        console.log('Category exists:', categoryExists);
        setSelectedCategory(categoryExists ? initialCategoryId : null);
        
        if (initialChildId) {
          const children = categories.find(c => c.id === initialCategoryId)?.children || [];
          const childExists = children.some(c => c.id === initialChildId);
          console.log('Child exists:', childExists, 'in children:', children);
          setSelectedChild(childExists ? initialChildId : null);
        } else {
          setSelectedChild(null);
        }
      } else {
        setSelectedCategory(null);
        setSelectedChild(null);
      }
    }
  }, [categories, initialCategoryId, initialChildId]);

  const loadCategories = async () => {
    try {
      const res = await getProductCategories({
        name: null,
        page: 1,
        size: 100
      }).unwrap();

      if (res.success && res.res?.content) {
        const mappedCategories = res.res.content.map((category) => ({
          ...category,
          parent: category.parent ? { 
            id: category.parent.id,
            childId: category.parent.childId || null 
          } : null,
          children: category.children || []
        }));
        setCategories(mappedCategories);
      }
    } catch (e) {
      console.error("Failed to load categories:", e);
    }
  };

  const getSelectedCategoryChildren = () => {
    const selectedCat = categories.find(c => c.id === selectedCategory);
    return selectedCat?.children || [];
  };

  const hasChildren = () => {
    const selectedCat = categories.find(c => c.id === selectedCategory);
    return selectedCat?.children && selectedCat.children.length > 0;  
  };

  const getCategoryOptions = () => {
    return categories.map(category => ({
      key: category.id,
      value: category.id,
      label: category.nameRu || category.nameKy || `Категория ${category.id}`
    }));
  };

  const getChildOptions = () => {
    const children = getSelectedCategoryChildren();
    return children.map(child => ({
      key: child.id,
      value: child.id,
      label: child.nameRu || child.nameKy || `Подкатегория ${child.id}`
    }));
  };

  return {
    categories,
    selectedCategory,
    setSelectedCategory,
    selectedChild,
    setSelectedChild,
    hasChildren,
    getCategoryOptions,
    getChildOptions
  };
};
